import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ContactRound, MessageSquare, Pencil, Plus, RefreshCw, Search, ShieldCheck } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import { fetchAdminPatients } from '@/lib/api/admin';
import { ensurePatientThread } from '@/lib/api/threads';
import { requestOpenThreadMessage } from '@/lib/notifications/notification-navigation';
import { formatAdminUserDisplayName, formatAdminUserDobDisplay, formatAdminUserSex, isPlatoSynced, primaryPhoneLineOrEmpty } from '@/lib/admin-user-display';
import { useHasPermission } from '@/hooks/use-permissions';
import { PatientEditorDialog } from '@/components/patients/patient-editor-dialog';
import { PatientCreateDialog } from '@/components/patients/patient-create-dialog';

const PAGE_SIZE = 50;

function initials(name) {
  return String(name || '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || '')
    .join('') || '?';
}

function PatientRow({ patient, onEdit, onMessage, isMessaging, canManage = true }) {
  const name = formatAdminUserDisplayName(patient) || 'Unknown patient';
  const phone = primaryPhoneLineOrEmpty(patient);
  const synced = isPlatoSynced(patient);
  const dob = formatAdminUserDobDisplay(patient);
  const sex = formatAdminUserSex(patient);

  return (
    <li className="flex items-center gap-3 px-5 py-3 hover:bg-muted/40">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-cyan-500/15 text-xs font-bold text-cyan-700 ring-1 ring-cyan-400/30 dark:text-cyan-300">
        {initials(name)}
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium text-foreground">{name}</span>
          {synced && (
            <Badge variant="secondary" className="gap-1 border-cyan-200 bg-cyan-50 text-[10px] text-cyan-700 dark:border-cyan-500/40 dark:bg-cyan-950/60 dark:text-cyan-300">
              <ShieldCheck className="h-3 w-3" /> Synced
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          {phone ? <span className="font-mono">{phone}</span> : <span className="italic">No phone</span>}
          {dob && dob !== '—' ? <span>DOB {dob}</span> : null}
          {sex ? <span>{sex}</span> : null}
        </div>
      </div>
      {/* FIXME: this need to show active threads or create chat ... */}
      {/* <Button type="button" variant="ghost" size="sm" onClick={() => onMessage(patient)} disabled={isMessaging} title="Start a conversation">
        {isMessaging ? <Spinner className="mr-2 h-4 w-4" /> : <MessageSquare className="mr-2 h-4 w-4" />}
        Message
      </Button> */}
      <Button type="button" variant="outline" size="sm" onClick={() => onEdit(patient)}>
        <Pencil className="mr-2 h-4 w-4" />
        {canManage ? 'Edit' : 'View'}
      </Button>
    </li>
  );
}

function PatientsPage() {
  // RBAC: patients:view renders the directory read-only; manage unlocks add/edit.
  const canManage = useHasPermission('patients', 'manage');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [search, setSearch] = useState('');
  const [editor, setEditor] = useState({ open: false, userId: null });
  const [createOpen, setCreateOpen] = useState(false);
  const [messagingId, setMessagingId] = useState(null);
  const debounceRef = useRef(null);

  const load = useCallback(async ({ search: term, skip, append } = {}) => {
    if (append) setIsLoadingMore(true);
    else setIsLoading(true);
    try {
      const res = await fetchAdminPatients({ search: term, skip, limit: PAGE_SIZE });
      setTotal(res.total);
      setItems((prev) => (append ? [...prev, ...res.items] : res.items));
    } catch (error) {
      toast.error(`Failed to load patients: ${error.message || error}`);
      if (!append) setItems([]);
    } finally {
      if (append) setIsLoadingMore(false);
      else setIsLoading(false);
    }
  }, []);

  // Debounced search (and initial load). Resets to the first page on every term change.
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      load({ search: search.trim(), skip: 0, append: false });
    }, search.trim() ? 300 : 0);
    return () => clearTimeout(debounceRef.current);
  }, [search, load]);

  const refresh = useCallback(() => {
    load({ search: search.trim(), skip: 0, append: false });
  }, [load, search]);

  const handleMessage = useCallback(async (patient) => {
    const userId = String(patient?.id || '').trim();
    if (!userId) return;
    setMessagingId(userId);
    try {
      const { threadId } = await ensurePatientThread({
        patientUserId: userId,
        phone: primaryPhoneLineOrEmpty(patient),
        displayName: formatAdminUserDisplayName(patient),
      });
      if (threadId) {
        requestOpenThreadMessage({ threadId });
      } else {
        toast.error('Could not open a conversation for this patient.');
      }
    } catch (error) {
      toast.error(error?.message || 'Could not start a conversation.');
    } finally {
      setMessagingId(null);
    }
  }, []);

  const canLoadMore = items.length < total;

  return (
    <div className="flex h-full min-h-0 w-full flex-col gap-6 p-6">
      <Card className="w-full shrink-0">
        <CardHeader className="space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-3 text-3xl">
                <ContactRound className="h-7 w-7 text-primary" />
                Patients
              </CardTitle>
              <p className="mt-2 text-sm text-muted-foreground">
                Search, add, and edit patients. {total} patient{total === 1 ? '' : 's'}.
              </p>
            </div>
            {canManage && (
              <Button type="button" onClick={() => setCreateOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                New patient
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="relative max-w-md flex-1">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground opacity-60" />
              <Input
                placeholder="Search by name or phone…"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="pl-8"
              />
            </div>
            <Button type="button" variant="ghost" size="sm" onClick={refresh} disabled={isLoading}>
              <RefreshCw className={cn('mr-2 h-4 w-4', isLoading && 'animate-spin')} />
              Refresh
            </Button>
          </div>
        </CardHeader>
      </Card>

      <Card className="flex min-h-0 w-full flex-1 flex-col overflow-hidden">
        <CardContent className="flex min-h-0 flex-1 flex-col p-0">
          <ScrollArea className="min-h-0 flex-1">
            <ul className="divide-y divide-border">
              {isLoading && items.length === 0 && (
                <li className="space-y-3 p-5">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <Skeleton className="h-9 w-9 rounded-full" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-40" />
                        <Skeleton className="h-3 w-56" />
                      </div>
                    </div>
                  ))}
                </li>
              )}

              {!isLoading && items.length === 0 && (
                <li className="flex flex-col items-center gap-3 p-12 text-center">
                  <ContactRound className="h-10 w-10 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    {search.trim() ? `No patients match “${search.trim()}”.` : 'No patients yet — add one to get started.'}
                  </p>
                  {canManage && (
                    <Button type="button" variant="outline" onClick={() => setCreateOpen(true)}>
                      <Plus className="mr-2 h-4 w-4" />
                      New patient
                    </Button>
                  )}
                </li>
              )}

              {items.map((patient) => (
                <PatientRow
                  key={patient.id}
                  patient={patient}
                  canManage={canManage}
                  isMessaging={messagingId === patient.id}
                  onEdit={(p) => setEditor({ open: true, userId: p.id })}
                  onMessage={handleMessage}
                />
              ))}

              {canLoadMore && (
                <li className="flex justify-center p-4">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={isLoadingMore}
                    onClick={() => load({ search: search.trim(), skip: items.length, append: true })}
                  >
                    {isLoadingMore ? <Spinner className="mr-2 h-4 w-4" /> : null}
                    Load more ({items.length} of {total})
                  </Button>
                </li>
              )}
            </ul>
          </ScrollArea>
        </CardContent>
      </Card>

      <PatientCreateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={() => refresh()}
      />

      <PatientEditorDialog
        open={editor.open}
        userId={editor.userId}
        readOnly={!canManage}
        onOpenChange={(open) => setEditor((cur) => ({ ...cur, open }))}
        onSaved={() => refresh()}
      />
    </div>
  );
}

export default PatientsPage;
